import React, { useState, useEffect } from "react";
import {
  Search, Filter, X, AlertTriangle,
  Users, Ban, Clock, Settings,
  FileText, Calendar, DollarSign, ShoppingBag,
  Phone, Mail, MapPin, User, Star, MessageSquare,
  Download, Trash2, Edit, Eye, CheckCircle, AlertOctagon,
  Timer, ArrowRight, Bell, Calculator, CheckSquare, FileCheck,
  PlusCircle, Archive, Send, Receipt, CircleDollarSign, ClipboardCheck
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { fetchUserOffers, updateOffer, deleteOffer } from "../../redux/Slices/offerSlice";
import RequestQuoteModal, { OfferStatusEnum } from "../../components/ui/RequestQuoteModal";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

const OffersManagementPage = () => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const { offers, loading, error, totalCount, currentPage: reduxCurrentPage, totalPages, hasNextPage } = useAppSelector((state: any) => state.offers);

  // State management
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [offerStatus, setOfferStatus] = useState("all");
  const [serviceType, setServiceType] = useState("all");
  const [showOfferDetails, setShowOfferDetails] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [editingOffer, setEditingOffer] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRequestQuoteModal, setShowRequestQuoteModal] = useState(false);

  // Fetch offers on component mount and when filters change
  useEffect(() => {
    fetchOffers();
  }, [currentPage, offerStatus, serviceType, sortBy, searchQuery]);

  const fetchOffers = async () => {
    try {
      await dispatch(fetchUserOffers({
        page: currentPage,
        limit: limit,
        status: offerStatus === "all" ? undefined : offerStatus as any,
        search: searchQuery || undefined,
        sort: [{ orderBy: "createdAt", order: "DESC" }]
      }));
    } catch (error) {
    }
  };

  // Handle offer status change
  const handleStatusChange = (newStatus: string) => {
    setOfferStatus(newStatus);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle offer edit
  const handleEditOffer = (offer: any) => {
    setEditingOffer(offer);
    setShowRequestQuoteModal(true);
  };

  // Handle offer update
  const handleUpdateOffer = async (updatedData: any) => {
    try {
      const result = await dispatch(updateOffer({ id: updatedData.id, data: updatedData })).unwrap();

      // Update the selected offer object if it's currently being viewed
      if (showOfferDetails && showOfferDetails.id === updatedData.id) {
        setShowOfferDetails({
          ...showOfferDetails,
          ...result
        });
      }

      // Update the editing offer if it's the same one
      if (editingOffer && editingOffer.id === updatedData.id) {
        setEditingOffer({
          ...editingOffer,
          ...result
        });
      }

      // Also update the offer in the offers list
      const updatedOffers = offers.map(offer =>
        offer.id === updatedData.id ? { ...offer, ...result } : offer
      );

      setShowEditModal(false);
      setEditingOffer(null);
      fetchOffers(); // Refresh the list
    } catch (error) {
    }
  };

  // Handle offer deletion
  const handleDeleteOffer = async (offerId: string) => {
    if (window.confirm("Are you sure you want to delete this offer?")) {
      try {
        await dispatch(deleteOffer(offerId));

        // Close the offer details modal if the deleted offer is currently being viewed
        if (showOfferDetails && showOfferDetails.id === offerId) {
          setShowOfferDetails(null);
        }

        // Clear editing offer if it's the same one
        if (editingOffer && editingOffer.id === offerId) {
          setEditingOffer(null);
        }

        fetchOffers(); // Refresh the list
      } catch (error) {
      }
    }
  };

  // Helper function to safely extract status value
  const getStatusValue = (status) => {
    if (typeof status === "object" && status !== null) {
      return status.name || status.id || "";
    }

    return status || "";
  };

  // Helper function to safely format status for display
  const formatStatusForDisplay = (status) => {
    const statusValue = getStatusValue(status);
    return String(statusValue).replace("_", " ").toUpperCase();
  };

  // Helper function to get status name by ID
  const getStatusName = (statusId) => {
    const statusMap = {
      1: "Draft",
      2: "Approved",
      3: "Approved",
      4: "Rejected",
      5: "Expired",
      6: "Suspended",
      7: "Completed",
      8: "Archived",
      9: "Deleted",
      10: "Permanently Deleted",
      11: "Suspended",
      12: "Rejected",
      13: "Admin Deleted",
    };
    return statusMap[statusId] || "Unknown";
  };

  // Helper function to update selected offer object after status change
  const updateSelectedOfferAfterStatusChange = (offerId: string, newStatus: number) => {
    // Update the selected offer object if it's currently being viewed
    if (showOfferDetails && showOfferDetails.id === offerId) {
      setShowOfferDetails({
        ...showOfferDetails,
        status: { id: newStatus, name: getStatusName(newStatus) }
      });
    }

    // Update the editing offer if it's the same one
    if (editingOffer && editingOffer.id === offerId) {
      setEditingOffer({
        ...editingOffer,
        status: { id: newStatus, name: getStatusName(newStatus) }
      });
    }
  };

  // Edit Offer Modal Component
  const EditOfferModal = ({ offer, isOpen, onClose, onSave }) => {
    if (!offer || !isOpen) return null;

    const [editData, setEditData] = useState({
      title: offer.title || "",
      description: offer.description || "",
      timeline: offer.timeline || 1,
      isWarranty: offer.isWarranty || false,
      isPartsIncluded: offer.isPartsIncluded || false
    });

    const handleSave = () => {
      onSave(editData);
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-md w-full">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Edit Offer</h3>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timeline
              </label>
              <input
                type="number"
                value={editData.timeline}
                onChange={(e) => setEditData({ ...editData, timeline: parseInt(e.target.value) || 1 })}
                min="1"
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editData.isWarranty}
                  onChange={(e) => setEditData({ ...editData, isWarranty: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">Warranty Included</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editData.isPartsIncluded}
                  onChange={(e) => setEditData({ ...editData, isPartsIncluded: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">Parts Included</span>
              </label>
            </div>
          </div>

          <div className="p-6 border-t flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Offer Details Modal Component
  const OfferDetailsModal = ({ offer, isOpen, onClose }) => {
    if (!offer || !isOpen) return null;

    const getStatusBadge = (status) => {
      // Extract the status value, handling both object and string formats
      const statusValue = typeof status === "object" ? status?.name : status;

      const styles = {
        under_review: "bg-blue-100 text-blue-700",
        approved: "bg-green-100 text-green-700",
        rejected: "bg-red-100 text-red-700",
        completed: "bg-purple-100 text-purple-700"
      };
      return styles[statusValue] || "bg-gray-100 text-gray-700";
    };

    // Helper function to check if file is an image
    const isImage = (file) => {
      const imageTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
      return imageTypes.includes(file.type?.toLowerCase()) ||
        file.name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|svg)$/);
    };

    // Helper function to check if file is a video
    const isVideo = (file) => {
      const videoTypes = ["video/mp4", "video/avi", "video/mov", "video/wmv", "video/flv", "video/webm"];
      return videoTypes.includes(file.type?.toLowerCase()) ||
        file.name?.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm)$/);
    };

    // Helper function to get file extension
    const getFileExtension = (filename) => {
      return filename?.split(".").pop()?.toLowerCase() || "";
    };

    // Helper function to download file
    const handleDownload = async (file) => {
      try {
        if (file.url || file.path) {
          const response = await fetch(file.url || file.path);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.name || `file-${file.id}.${getFileExtension(file.name)}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          // Fallback for files without direct URL
          const a = document.createElement("a");
          a.href = `data:${file.type || "application/octet-stream"};base64,${file.content || ""}`;
          a.download = file.name || `file-${file.id}.${getFileExtension(file.name)}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      } catch (error) {
        console.error("Download failed:", error);
        alert("Download failed. Please try again.");
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ textAlign: "left" }}>
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 border-b sticky top-0 bg-white">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3">
                  <Badge variant={undefined} className={getStatusBadge(offer.status)}>
                    {formatStatusForDisplay(offer.status)}
                  </Badge>
                  {offer.timelineType?.name && (
                    <Badge variant="outline" className="border-purple-200 text-purple-700">
                      {offer.timelineType.name.toUpperCase()}
                    </Badge>
                  )}
                </div>
                <h2 className="text-xl font-semibold mt-2">{offer.title || "Offer Title"}</h2>
                <p className="text-sm text-gray-500">Offer ID: {offer.id}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Offer Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CircleDollarSign className="h-5 w-5 text-blue-600" />
                  <div>
                    <span className="font-medium text-blue-800">Proposed Price: {offer.job?.currency?.initial} {offer.job?.budget ?? "N/A"} </span>
                    {offer.isWarranty && (
                      <span className="ml-2 text-sm text-blue-600">
                        (Warranty Included)
                      </span>
                    )}
                  </div>
                </div>
                <Badge variant={undefined} className={`${Number(offer.status.id) === 2 ? "bg-yellow-100 text-yellow-700" : Number(offer.status.id) === 3 ? "bg-green-100 text-green-700" : Number(offer.status.id) === 4 ? "bg-red-100 text-red-700" : Number(offer.status.id) === 5 ? "bg-gray-100 text-gray-700" : Number(offer.status.id) === 6 ? "bg-blue-100 text-blue-700" : Number(offer.status.id) === 7 ? "bg-purple-100 text-purple-700" : Number(offer.status.id) === 8 ? "bg-orange-100 text-orange-700" : Number(offer.status.id) === 9 ? "bg-red-100 text-red-700" : Number(offer.status.id) === 10 ? "bg-red-900 text-white" : "bg-blue-100 text-blue-700"}`}>
                  Status: {Number(offer.status.id) === 2 ? "Approved" : Number(offer.status.id) === 3 ? "Accepted" : Number(offer.status.id) === 4 ? "Rejected" : Number(offer.status.id) === 5 ? "Expired" : Number(offer.status.id) === 6 ? "Cancelled" : Number(offer.status.id) === 7 ? "Completed" : Number(offer.status.id) === 8 ? "Archived" : Number(offer.status.id) === 9 ? "Deleted" : Number(offer.status.id) === 10 ? "Permanently Deleted" : Number(offer.status.id) === 11 ? "Suspended" : Number(offer.status.id) === 12 ? "Rejected" : Number(offer.status.id) === 13 ? "Admin Deleted" : Number(offer.status.id) === 15 ? "Contracted" : "Unknown"}
                </Badge>
              </div>
            </div>

            {/* User Information */}
            <div className="space-y-4">
              <h3 className="font-medium">Customer Information</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Name</span>
                    <p className="font-medium">{offer.job?.userId?.firstName + " " + offer.job?.userId?.lastName || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Phone</span>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{offer.job?.userId?.phone || "N/A"}</p>
                      <Button size="sm" variant="outline" className="h-8">
                        <Phone className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Email</span>
                    <p className="font-medium">{offer.job?.userId?.email}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Address</span>
                    <p className="font-medium">{offer.job?.userId?.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Provider Information */}
            <div className="space-y-4">
              <h3 className="font-medium">Service Provider</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">{offer.user.firstName + " " + offer.user.lastName}</h4>
                      <p className="text-sm text-gray-500">{offer.user.title}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-400 fill-current" />
                    <span>0</span>
                    {(
                      <Badge variant={undefined} className="bg-green-100 text-green-700">Verified</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Quote Details */}
            <div className="space-y-4">
              <h3 className="font-medium">Service Details</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div>
                  <span className="text-sm text-gray-500">Description</span>
                  <p className="mt-1">{offer.description || "No description provided"}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <CheckSquare className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Warranty: {offer.isWarranty ? "Yes" : "No"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckSquare className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Parts Included: {offer.isPartsIncluded ? "Yes" : "No"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {offer.files?.length || 0} attachments
                  </span>
                </div>
              </div>
            </div>

            {/* Media Files Section */}
            {offer.files && offer.files.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-medium">Attachments & Media Files</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {offer.files.map((file, index) => (
                    <div key={index} className="border rounded-lg overflow-hidden bg-gray-50">
                      {isImage(file) ? (
                        <div className="relative group">
                          <img
                            src={file.url || file.path || `data:${file.type};base64,${file.content}`}
                            alt={file.name || `Image ${index + 1}`}
                            className="w-full h-32 object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCAxMDAgTDEwMCA2MCBMMTQwIDEwMEg2MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIwIDIwQzIwIDE3LjggMTcuOCAxNSAxNSAxNUMxMi4yIDE1IDEwIDE3LjIgMTAgMjBDMTAgMjIuOCAxMi4yIDI1IDE1IDI1QzE3LjggMjUgMjAgMjIuOCAyMCAyMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+Cjwvc3ZnPgo=";
                            }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                            <button
                              onClick={() => handleDownload(file)}
                              className="opacity-0 group-hover:opacity-100 bg-white text-gray-800 px-3 py-2 rounded-lg shadow-lg hover:bg-gray-100 transition-all duration-200 flex items-center gap-2"
                            >
                              <Download className="h-4 w-4" />
                              Download
                            </button>
                          </div>
                        </div>
                      ) : isVideo(file) ? (
                        <div className="relative group">
                          <video
                            src={file.url || file.path || `data:${file.type};base64,${file.content}`}
                            className="w-full h-32 object-cover"
                            controls
                            preload="metadata"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                            <button
                              onClick={() => handleDownload(file)}
                              className="opacity-0 group-hover:opacity-100 bg-white text-gray-800 px-3 py-2 rounded-lg shadow-lg hover:bg-gray-100 transition-all duration-200 flex items-center gap-2"
                            >
                              <Download className="h-4 w-4" />
                              Download
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 text-center">
                          <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600 truncate">{file.name || `File ${index + 1}`}</p>
                          <p className="text-xs text-gray-500">{getFileExtension(file.name)}</p>
                        </div>
                      )}
                      <div className="p-3 border-t">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700 truncate">
                            {file.name || `File ${index + 1}`}
                          </span>
                          <button
                            onClick={() => handleDownload(file)}
                            className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded"
                            title="Download file"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {file.type || "Unknown type"} • {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "Size unknown"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Admin Actions */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium">Offer Actions</h3>
              <div className="flex flex-wrap gap-3">
                <Button
                  className="flex items-center gap-2"
                  onClick={() => handleEditOffer(offer)}
                >
                  <Edit className="h-4 w-4" />
                  Edit Offer
                </Button>

                {(Number(offer.status?.id) === 3 || Number(offer.status?.id) === 2) && (
                  <>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 text-orange-600"
                      onClick={() => handleUpdateOffer({ ...offer, timelineType: { id: Number(offer.timelineType?.id) }, status: { id: OfferStatusEnum.Admin_Suspended } })}
                    >
                      <AlertTriangle className="h-4 w-4" />
                      Suspend Offer
                    </Button>
                    {Number(offer.status?.id) === 3 && <Button
                      variant="outline"
                      className="flex items-center gap-2 text-purple-600"
                      onClick={() => handleUpdateOffer({ ...offer, timelineType: { id: Number(offer.timelineType?.id) }, status: { id: 7 } })}
                    >
                      <CheckSquare className="h-4 w-4" />
                      Complete Offer
                    </Button>}
                  </>
                )}
                {/* Show different buttons based on offer status */}
                {Number(offer.status?.id) === 2 && (
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 text-red-600"
                    onClick={() => handleUpdateOffer({ ...offer, timelineType: { id: Number(offer.timelineType?.id) }, status: { id: 4 } })}
                  >
                    <X className="h-4 w-4" />
                    Reject Offer
                  </Button>
                )}

                {Number(offer.status?.id) === 6 && (
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 text-green-600"
                    onClick={() => handleUpdateOffer({ ...offer, timelineType: { id: Number(offer.timelineType?.id) }, status: { id: 3 } })}
                  >
                    <CheckCircle className="h-4 w-4" />
                    Activate Offer
                  </Button>
                )}

                {(Number(offer.status?.id) === 4 || Number(offer.status?.id) === 6 || Number(offer.status?.id) === OfferStatusEnum.Admin_Deleted || Number(offer.status?.id) === OfferStatusEnum.Admin_Suspended) && (
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 text-green-600"
                    onClick={() => handleUpdateOffer({ ...offer, timelineType: { id: Number(offer.timelineType?.id) }, status: { id: 2 } })}
                  >
                    <CheckCircle className="h-4 w-4" />
                    Approve Offer
                  </Button>
                )}

                {Number(offer.status?.id) === 9 || Number(offer.status?.id) === OfferStatusEnum.Admin_Deleted && (
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 text-green-600"
                    onClick={() => handleUpdateOffer({ ...offer, timelineType: { id: Number(offer.timelineType?.id) }, status: { id: 2 } })}
                  >
                    <CheckCircle className="h-4 w-4" />
                    Restore Offer
                  </Button>
                )}

                {/* Delete button - only show for non-deleted offers */}
                {Number(offer.status?.id) !== 9 && Number(offer.status?.id) !== OfferStatusEnum.Admin_Deleted && (
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 text-red-600"
                    onClick={() => handleUpdateOffer({ ...offer, timelineType: { id: Number(offer.timelineType?.id) }, status: { id: OfferStatusEnum.Admin_Deleted } })}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Offer
                  </Button>
                )}
                {Number(offer.status?.id) === OfferStatusEnum.Admin_Deleted && (
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 text-red-600"
                    onClick={() => handleUpdateOffer({ ...offer, timelineType: { id: Number(offer.timelineType?.id) }, status: { id: OfferStatusEnum.Admin_Permanent_Deleted } })}
                  >
                    <Trash2 className="h-4 w-4" />
                    Permanently Delete
                  </Button>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Offers Management</h1>
                <p className="text-gray-600 mt-1">Review and manage service offers</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export Offers
                </Button>
                <Button className="flex items-center gap-2">
                  <PlusCircle className="h-4 w-4" />
                  Create Offer
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Offers</p>
                    <p className="text-2xl font-semibold">{totalCount || 0}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Pending Review</p>
                    <p className="text-2xl font-semibold">
                      {offers?.filter(o => o.status?.name === "approved" || o.status === "approved").length || 0}
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Approved</p>
                    <p className="text-2xl font-semibold">
                      {offers?.filter(o => o.status?.name === "approved" || o.status === "approved").length || 0}
                    </p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Current Page</p>
                    <p className="text-2xl font-semibold">{currentPage}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Calculator className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
                  <div className="relative flex-1 w-full sm:w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search offers..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full lg:w-auto">
                  <select
                    value={offerStatus}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Status</option>
                    <option value="approved">Approved</option>
                    <option value="under_review">Under Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="completed">Completed</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="price_high">Highest Price</option>
                    <option value="price_low">Lowest Price</option>
                  </select>
                </div>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quote Type
                      </label>
                      <select className="w-full px-4 py-2 border rounded-lg">
                        <option value="all">All Types</option>
                        <option value="standard">Standard</option>
                        <option value="custom">Custom</option>
                        <option value="emergency">Emergency</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Type
                      </label>
                      <select
                        value={serviceType}
                        onChange={(e) => setServiceType(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Services</option>
                        <option value="plumbing">Plumbing</option>
                        <option value="electrical">Electrical</option>
                        <option value="hvac">HVAC</option>
                        <option value="cleaning">Cleaning</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price Range
                      </label>
                      <select className="w-full px-4 py-2 border rounded-lg">
                        <option value="all">All Ranges</option>
                        <option value="0-100">{getDynamicCurrencySymbol()}0 - {getDynamicCurrencySymbol()}100</option>
                        <option value="101-500">{getDynamicCurrencySymbol()}101 - {getDynamicCurrencySymbol()}500</option>
                        <option value="501-1000">{getDynamicCurrencySymbol()}501 - {getDynamicCurrencySymbol()}1000</option>
                        <option value="1000+">{getDynamicCurrencySymbol()}1000+</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Offers Table */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quote Info
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Provider
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center">
                            <Clock className="h-5 w-5 animate-spin mr-2" />
                            Loading offers...
                          </div>
                        </td>
                      </tr>
                    ) : offers && offers.length > 0 ? (
                      offers.map((offer) => (
                        <tr key={offer.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-blue-500" />
                              </div>
                              <div className="ml-4">
                                <div className="font-medium text-gray-900">{offer.id}</div>
                                <div className="text-sm text-gray-500">{offer.title || "No Title"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{offer.user?.firstName + " " + offer.user?.lastName || "N/A"}</div>
                            <div className="text-sm text-gray-500">{offer.user?.title || "N/A"}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{offer.job?.userId?.firstName + " " + offer.job?.userId?.lastName || "No Name"}</div>
                            <div className="text-sm text-gray-500">{offer.job?.userId?.title || "N/A"}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={undefined} className={
                              Number(offer.status.id) === 2 ? "bg-yellow-100 text-yellow-700" :
                                Number(offer.status.id) === 3 ? "bg-green-100 text-green-700" :
                                  Number(offer.status.id) === 10 ? "bg-red-900 text-white" :
                                    "bg-purple-100 text-purple-700"
                            }>
                              {Number(offer.status.id) === 2 ? "Approved" : Number(offer.status.id) === 3 ? "Accepted" : Number(offer.status.id) === 4 ? "Rejected" : Number(offer.status.id) === 5 ? "Expired" : Number(offer.status.id) === 6 ? "Cancelled" : Number(offer.status.id) === 7 ? "Completed" : Number(offer.status.id) === 8 ? "Archived" : Number(offer.status.id) === 9 ? "Deleted" : Number(offer.status.id) === 10 ? "Permanently Deleted" : Number(offer.status.id) === 11 ? "Suspended" : Number(offer.status.id) === 12 ? "Rejected" : Number(offer.status.id) === 13 ? "Admin Deleted" : Number(offer.status.id) === 15 ? "Contracted" : "Unknown"}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {offer.job?.currency?.initial} {offer.job?.budget ?? "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowOfferDetails(offer)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                View Details
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                          No offers found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {(totalCount > limit || hasNextPage) && (
                <div className="px-6 py-4 border-t flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">
                      Showing page {currentPage} of {totalPages || Math.ceil(totalCount / limit)} ({totalCount || 0} total offers)
                      {hasNextPage && (
                        <span className="ml-2 text-blue-600">
                          • More pages available
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    {/* Page Number Buttons */}
                    <div className="flex items-center gap-1 flex-wrap justify-center">
                      {Array.from({ length: totalPages || Math.ceil(totalCount / limit) }, (_, i) => {
                        const page = i + 1;
                        const maxPages = totalPages || Math.ceil(totalCount / limit);
                        if (page > maxPages) return null;

                        return (
                          <Button
                            key={page}
                            variant={page === currentPage ? "default" : "outline"}
                            size="sm"
                            className="min-w-[40px]"
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>

                    {/* Previous/Next Buttons */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={!hasNextPage && currentPage >= (totalPages || 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>

      {/* Offer Details Modal */}
      <OfferDetailsModal
        offer={showOfferDetails}
        isOpen={!!showOfferDetails}
        onClose={() => setShowOfferDetails(null)}
      />

      {/* Request Quote Modal for Editing Offers */}
      <RequestQuoteModal
        isOpen={showRequestQuoteModal}
        onClose={() => {
          setShowRequestQuoteModal(false);
          setEditingOffer(null);
        }}
        professional={editingOffer?.user || {}}
        offerId={editingOffer?.id}
        existingOffer={editingOffer}
        userJobs={[]}
        onOfferCreated={(offer) => {
          setShowRequestQuoteModal(false);
          setEditingOffer(null);
          fetchOffers();
        }}
        onOfferUpdated={(offer) => {

          // Update the selected offer object if it's currently being viewed
          if (showOfferDetails && showOfferDetails.id === offer.id) {
            setShowOfferDetails({
              ...showOfferDetails,
              ...offer
            });
          }

          setShowRequestQuoteModal(false);
          setEditingOffer(null);
          fetchOffers();
        }}
      />

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default OffersManagementPage;